import { Users, Target, Heart } from "lucide-react";

export const MissionSection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-accent/10 via-background to-primary/10 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_hsl(var(--accent)/0.1),transparent_50%)]" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto space-y-16">
          {/* Header */}
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-accent/10 backdrop-blur-sm rounded-full text-accent font-semibold border border-accent/20 mb-4">
              <Heart className="w-5 h-5" />
              <span>Our Mission</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
              Second Chance Connect
            </h2>
            <p className="text-2xl md:text-3xl text-accent font-semibold">
              Breaking down barriers to employment for people with criminal records across the UK.
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-8 text-lg text-muted-foreground leading-relaxed">
            <p>
              For millions of people across the UK, a past conviction serves a second sentence: the closed door of unemployment. 
              Talent, ambition, and the desire to contribute are instantly dismissed by a single tick box on an application form. 
              This creates a devastating cycle of exclusion that wastes human potential, increases reoffending, and weakens our communities.
            </p>

            <p className="text-xl md:text-2xl text-foreground font-semibold text-center py-6">
              We believe that a person's potential should define their future, not their past.
            </p>

            <p>
              Second Chance Connect is a purpose-built platform designed to fundamentally shift this dynamic. 
              We are creating a new ecosystem where skills, motivation, and character are the first things an employer sees. 
              By connecting forward-thinking companies with a motivated and untapped talent pool, we replace prejudice with opportunity.
            </p>
          </div>

          {/* How We Do It */}
          <div className="space-y-8">
            <h3 className="text-3xl font-bold text-foreground text-center">How We Do It</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* For Job Seekers */}
              <div className="bg-card border border-border rounded-2xl p-8 space-y-4 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elevated)] transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-accent/10 rounded-full">
                    <Users className="w-6 h-6 text-accent" />
                  </div>
                  <h4 className="text-2xl font-bold text-foreground">For Job Seekers</h4>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  We provide a safe, empowering environment to showcase your skills. Our unique Progressive Disclosure model 
                  ensures you are judged on your abilities first, allowing you to build a rapport with an employer and share 
                  your story on your terms, only after mutual interest is established.
                </p>
              </div>

              {/* For Employers */}
              <div className="bg-card border border-border rounded-2xl p-8 space-y-4 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-elevated)] transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-accent/10 rounded-full">
                    <Target className="w-6 h-6 text-accent" />
                  </div>
                  <h4 className="text-2xl font-bold text-foreground">For Employers</h4>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  We offer direct access to a diverse and resilient talent pool, ready to bring value and loyalty to your business. 
                  We provide the tools and support to help you build a more inclusive, effective, and successful workforce.
                </p>
              </div>
            </div>
          </div>

          {/* Closing Statement */}
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-accent/20 rounded-2xl p-10 space-y-6 text-center shadow-[var(--shadow-card)]">
            <div className="flex justify-center">
              <div className="p-4 bg-accent/10 rounded-full">
                <Heart className="w-10 h-10 text-accent" />
              </div>
            </div>
            <p className="text-xl text-foreground leading-relaxed max-w-3xl mx-auto">
              By bridging this gap, we are not just filling jobs; we are rebuilding lives, strengthening families, 
              and powering the UK economy.
            </p>
            <p className="text-2xl font-bold text-accent">
              Join us in creating a fairer future where everyone has the chance to succeed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
